import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Radar, Cpu, CircleDot, ShieldCheck, ShieldAlert, Loader2, Lock, Radio, Camera, FlaskConical, Play, Clapperboard } from "lucide-react";
import ScenarioSwitcher from "./ScenarioSwitcher";

// Top context header. Distinguishes a transient "reconnecting" state from a
// terminal "authorization lost" state (item 7) — they are visually different and
// only auth-loss stops retrying. The Live chip is always SCOPED to a Run id.
export default function ContextHeader({
  selected, scenarios, runs, onSelect, recon, quantum, integrityOk, summary, connection,
  live, lastEventAt, snapshot, classification, canStartLive, starting, onStartLive, demo, onOpenDemo,
}) {
  const { t } = useTranslation();
  const scenarioKey = scenarios.find((s) => s.run_id === selected?.run_id)?.key || recon?.run?.demo_scenario || scenarios[0]?.key;
  return (
    <div className="border-b border-[var(--qn-border)] bg-[var(--qn-background-secondary)] px-6 py-3" data-testid="dg-context-header">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Radar className="w-5 h-5 text-[var(--qn-primary)] shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate text-[var(--qn-foreground)]">{selected?.label || t("decisionGraph.title")}</div>
            <div className="text-[11px] font-mono text-[var(--qn-foreground-subtle)] truncate" data-testid="dg-run-status-line">
              {selected?.run_id ? `${selected.run_id} · ${recon?.run?.status || "…"}` : t("decisionGraph.selectScenario")}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ScenarioSwitcher scenarios={scenarios} runs={runs} selected={selected} onSelect={onSelect} />
          {canStartLive && scenarioKey && (
            <button data-testid="dg-start-live-btn" disabled={starting || live} onClick={() => onStartLive(scenarioKey)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-[var(--qn-error)] text-[var(--qn-error)] hover:bg-[var(--qn-error-muted)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--qn-primary)]">
              {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
              {t("decisionGraph.live.start")}
            </button>
          )}
          {canStartLive && scenarioKey && !demo && (
            <button data-testid="dg-investor-demo-btn" disabled={starting || live} onClick={() => onOpenDemo(scenarioKey)}
              title={t("decisionGraph.demo.hint")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-[var(--qn-primary)] text-[var(--qn-primary)] hover:bg-[var(--qn-primary-muted)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--qn-primary)]">
              <Clapperboard className="w-3.5 h-3.5" />{t("decisionGraph.demo.start")}
            </button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2.5 flex-wrap">
        {live && selected?.run_id && (
          <Chip icon={Radio} tone="live" testid="dg-live-chip">
            <span className="relative flex h-2 w-2 mr-0.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--qn-error)] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--qn-error)]" />
            </span>
            {t("decisionGraph.live.tailing", { id: selected.run_id })}
            <LastEventAge at={lastEventAt} />
          </Chip>
        )}
        {demo && <Chip icon={Clapperboard} tone="neutral" testid="dg-demo-mode-chip">{t("decisionGraph.demo.chip", { scenario: demo.scenario.toUpperCase() })}</Chip>}
        {snapshot && <Chip icon={Camera} tone="neutral" testid="dg-snapshot-chip">{t("decisionGraph.snapshot.chip", { seq: snapshot.sequence })}</Chip>}
        {classification === "demo" && <Chip icon={FlaskConical} tone="muted" testid="dg-demo-chip">{t("decisionGraph.chips.demoData")}</Chip>}
        <Chip icon={Cpu} tone="neutral" testid="dg-engine-chip">
          {summary.engine_class
            ? t("decisionGraph.chips.engine", { engineClass: summary.engine_class, engine: summary.selected_engine || "engine" })
            : t("decisionGraph.chips.engineUnavailable")}
        </Chip>
        {quantum
          ? <Chip icon={CircleDot} tone="quantum" testid="dg-quantum-badge">{t("decisionGraph.chips.quantumVerified")}</Chip>
          : <Chip icon={CircleDot} tone="muted" testid="dg-quantum-note">{t("decisionGraph.chips.quantumGated")}</Chip>}
        {recon && (integrityOk
          ? <Chip icon={ShieldCheck} tone="ok" testid="dg-integrity-badge">{t("decisionGraph.chips.integrityVerified")}</Chip>
          : <Chip icon={ShieldAlert} tone="warn" testid="dg-integrity-badge">{t("decisionGraph.chips.integrityUnverified")}</Chip>)}
        {connection === "reconnecting" && (
          <Chip icon={Loader2} tone="muted" testid="dg-reconnecting" spin>{t("decisionGraph.reconnecting")}</Chip>
        )}
        {connection === "auth_lost" && (
          <Chip icon={Lock} tone="warn" testid="dg-auth-lost">{t("decisionGraph.authLost")}</Chip>
        )}
      </div>
    </div>
  );
}

function LastEventAge({ at }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  if (!at) return <span className="ml-1 opacity-70" data-testid="dg-live-waiting">· {t("decisionGraph.live.waiting")}</span>;
  return <span className="ml-1 opacity-70 font-mono" data-testid="dg-live-age">· {t("decisionGraph.live.lastEvent", { s: Math.max(0, Math.round((now - at) / 1000)) })}</span>;
}

function Chip({ icon: Icon, tone, children, testid, spin }) {
  const tones = {
    neutral: "border-[var(--qn-border)] text-[var(--qn-foreground-muted)]",
    muted: "border-[var(--qn-border)] text-[var(--qn-foreground-subtle)]",
    ok: "border-[var(--qn-success)] text-[var(--qn-success)] bg-[var(--qn-success-muted)]",
    warn: "border-[var(--qn-error)] text-[var(--qn-error)] bg-[var(--qn-error-muted)]",
    quantum: "border-[var(--qn-secondary)] text-[var(--qn-secondary)] bg-[var(--qn-secondary-muted)]",
    live: "border-[var(--qn-error)] text-[var(--qn-foreground)] bg-[var(--qn-error-muted)] font-medium",
  };
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] ${tones[tone]}`}>
      <Icon className={`w-3.5 h-3.5 ${spin ? "animate-spin" : ""}`} />{children}
    </span>
  );
}
